/**
 * Item Categories - Centralized Source
 * =====================================
 * 
 * This module provides a single source of truth for item categories.
 * All components should import from here instead of hardcoding categories.
 * 
 * Usage:
 *   import { useCategories, CATEGORY_OPTIONS, getCategoryLabel } from '@/lib/categories';
 *   
 *   // In a component:
 *   const { categories, loading, isManufacturable } = useCategories();
 * 
 * The categories are fetched from the backend once and cached.
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache for categories data
let categoriesCache = null;
let cachePromise = null;

/**
 * Fetch categories from backend (with caching)
 */
export const fetchCategories = async (authHeader) => {
  // Return cached data if available
  if (categoriesCache) {
    return categoriesCache;
  }
  
  // If a fetch is already in progress, wait for it
  if (cachePromise) {
    return cachePromise;
  }
  
  // Start new fetch
  cachePromise = axios.get(`${API_URL}/api/items/categories`, {
    headers: authHeader
  }).then(response => {
    categoriesCache = response.data;
    cachePromise = null;
    return categoriesCache;
  }).catch(error => {
    console.error('Failed to fetch categories:', error);
    cachePromise = null;
    // Return fallback data
    return getFallbackCategories();
  });
  
  return cachePromise;
};

/**
 * Clear the categories cache (call after logout or when needed)
 */
export const clearCategoriesCache = () => {
  categoriesCache = null;
  cachePromise = null;
};

/**
 * Fallback categories in case API fails
 * This ensures the app doesn't break if the endpoint is unavailable
 */
const getFallbackCategories = () => ({
  categories: [
    { value: 'RMT', label: 'RMT - Raw Material', code: 'RMT', purchasable: true, sellable: false, manufacturable: false, is_asset: false, color: 'blue' },
    { value: 'SFG', label: 'SFG - Semi-Finished Goods', code: 'SFG', purchasable: true, sellable: true, manufacturable: true, is_asset: false, color: 'yellow' },
    { value: 'FNG', label: 'FNG - Finished Goods', code: 'FNG', purchasable: true, sellable: true, manufacturable: true, is_asset: false, color: 'green' },
    { value: 'CTM', label: 'CTM - Consumables/Tools/Machinery', code: 'CTM', purchasable: true, sellable: false, manufacturable: false, is_asset: false, color: 'purple' },
    { value: 'ASSET', label: 'ASSET - Asset', code: 'ASSET', purchasable: true, sellable: true, manufacturable: false, is_asset: true, color: 'orange' }
  ],
  valid_codes: ['RMT', 'SFG', 'FNG', 'CTM', 'ASSET'],
  sellable: ['SFG', 'FNG', 'ASSET'],
  manufacturable: ['SFG', 'FNG'],
  purchasable: ['RMT', 'SFG', 'FNG', 'CTM', 'ASSET']
});

/**
 * React hook for using categories
 * 
 * @param {function} getAuthHeader - Function that returns auth header object
 * @returns {object} { categories, validCodes, sellable, manufacturable, purchasable, loading, error, ... }
 */
export const useCategories = (getAuthHeader) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const authHeader = getAuthHeader ? getAuthHeader() : {};
        const result = await fetchCategories(authHeader);
        setData(result);
      } catch (err) {
        setError(err);
        setData(getFallbackCategories());
      } finally {
        setLoading(false);
      }
    };
    
    loadCategories();
  }, [getAuthHeader]);
  
  // Helper functions
  const isManufacturable = useCallback((categoryCode) => {
    return data?.manufacturable?.includes(categoryCode?.toUpperCase()) || false;
  }, [data]);
  
  const isSellable = useCallback((categoryCode) => {
    return data?.sellable?.includes(categoryCode?.toUpperCase()) || false;
  }, [data]);
  
  const isPurchasable = useCallback((categoryCode) => {
    return data?.purchasable?.includes(categoryCode?.toUpperCase()) || false;
  }, [data]);
  
  const isAsset = useCallback((categoryCode) => {
    const config = data?.config?.[categoryCode?.toUpperCase()];
    return config?.is_asset || false;
  }, [data]);
  
  const getCategoryConfig = useCallback((categoryCode) => {
    return data?.config?.[categoryCode?.toUpperCase()] || null;
  }, [data]);
  
  const getCategoryLabel = useCallback((categoryCode) => {
    const config = data?.config?.[categoryCode?.toUpperCase()];
    return config?.label || categoryCode;
  }, [data]);
  
  return {
    // Raw data
    categories: data?.categories || [],
    validCodes: data?.valid_codes || [],
    sellable: data?.sellable || [],
    manufacturable: data?.manufacturable || [],
    purchasable: data?.purchasable || [],
    config: data?.config || {},
    
    // Loading state
    loading,
    error,
    
    // Helper functions
    isManufacturable,
    isSellable,
    isPurchasable,
    isAsset,
    getCategoryConfig,
    getCategoryLabel,
    
    // For Select/Dropdown options
    categoryOptions: data?.categories || [],
    
    // Simple value/label format for basic dropdowns
    simpleCategoryOptions: (data?.categories || []).map(c => ({
      value: c.value || c.code,
      label: c.label
    }))
  };
};

/**
 * Get category options for dropdowns (synchronous, uses cache)
 * Note: Only works after categories have been fetched at least once
 */
export const getCategoryOptions = () => {
  if (categoriesCache) {
    return categoriesCache.categories || [];
  }
  return getFallbackCategories().categories;
};

/**
 * Get valid category codes (synchronous, uses cache)
 */
export const getValidCategoryCodes = () => {
  if (categoriesCache) {
    return categoriesCache.valid_codes || [];
  }
  return getFallbackCategories().valid_codes;
};

/**
 * Check if a category is valid
 */
export const isValidCategory = (code) => {
  const validCodes = getValidCategoryCodes();
  return validCodes.includes(code?.toUpperCase());
};

/**
 * Get display label for a category code
 */
export const getCategoryLabel = (code) => {
  if (!code) return '';
  const options = getCategoryOptions();
  const found = options.find(c => c.value === code || c.code === code);
  return found?.label || code;
};

/**
 * Get short label (just the code name)
 */
export const getCategoryShortLabel = (code) => {
  if (!code) return '';
  const upperCode = code.toUpperCase();
  const labels = {
    'RMT': 'Raw Material',
    'SFG': 'Semi-Finished',
    'FNG': 'Finished Goods',
    'CTM': 'Consumables',
    'ASSET': 'Asset'
  };
  return labels[upperCode] || code;
};

/**
 * Get category color for badges
 */
export const getCategoryColor = (code) => {
  if (!code) return 'gray';
  const colors = {
    'RMT': 'blue',
    'SFG': 'yellow', 
    'FNG': 'green',
    'CTM': 'purple',
    'ASSET': 'orange'
  };
  return colors[code.toUpperCase()] || 'gray';
};

/**
 * Static category codes for quick reference
 * Use these instead of hardcoding strings
 */
export const CATEGORIES = {
  RAW_MATERIAL: 'RMT',
  SEMI_FINISHED: 'SFG',
  FINISHED_GOODS: 'FNG',
  CONSUMABLES: 'CTM',
  ASSET: 'ASSET'
};

/**
 * Legacy support - static options for components that can't use hooks
 * PREFER using useCategories() hook instead
 */
export const CATEGORY_OPTIONS = [
  { value: 'RMT', label: 'RMT - Raw Material' },
  { value: 'SFG', label: 'SFG - Semi-Finished Goods' },
  { value: 'FNG', label: 'FNG - Finished Goods' },
  { value: 'CTM', label: 'CTM - Consumables/Tools/Machinery' },
  { value: 'ASSET', label: 'ASSET - Asset' }
];

/**
 * Category codes array for filters
 */
export const CATEGORY_CODES = ['RMT', 'SFG', 'FNG', 'CTM', 'ASSET'];

export default {
  useCategories,
  fetchCategories,
  clearCategoriesCache,
  getCategoryOptions,
  getValidCategoryCodes,
  isValidCategory,
  getCategoryLabel,
  getCategoryShortLabel,
  getCategoryColor,
  CATEGORIES,
  CATEGORY_OPTIONS,
  CATEGORY_CODES
};
